import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import Button from '@material-ui/core/Button';

import {
    StyledCard,
    StyledCardContent,
    Filter,
    Text,
    Tag,
    AvatarGroup,
    NoInformation,
} from '../../index';

import { StyledDayContainer } from './style';

import { alertError } from '../../../redux/stateActions';
import { GET_ENDED_SESSIONS } from '../../../services/provider';

import { SESSION_COLORS, SESSION_TYPE } from '../../../constant';

const TrackReport = ({ staffOptions, selectedStaff, setSelectedStaff }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    /*--------------- Get Ended session ---------------*/
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [endedSession, setEndedSession] = useState([]);

    const variables = useMemo(() => {
        const newVariables = {
            hospital: user.staff.hospital.id,
            startDate: startDate,
            endDate: endDate,
        };

        if (selectedStaff !== '') {
            newVariables.staff = selectedStaff;
        }
        return newVariables;
    }, [selectedStaff, startDate, endDate]);

    const { data: getEndedSessionData, error: getEndedSessionError } = useQuery(
        GET_ENDED_SESSIONS,
        {
            variables: variables,
            skip: _.isEmpty(user) || !startDate || !endDate,
            fetchPolicy: "network-only",
        }
    );

    useEffect(() => {
        if (getEndedSessionError) {
            dispatch(alertError(`Cannot get Open session: ${getEndedSessionError.message}`));
        }

        if (getEndedSessionData) {
            const newEndedSession = [];

            getEndedSessionData.sessions.forEach((session) => {
                if (!newEndedSession.some((meet) => meet.id === session.meet.id)) {
                    newEndedSession.push(session);
                }
            });

            setEndedSession(newEndedSession);
        }
    }, [getEndedSessionError, getEndedSessionData]);

    const handleFilterChange = ({ isToday, selectedMonth, selectedYear }) => {
        if (isToday) {
            setStartDate(dayjs().startOf('date').format('YYYY-MM-DD'));
            setEndDate(dayjs().endOf('date').format('YYYY-MM-DD'));
        } else {
            setStartDate(
                dayjs()
                    .set({ month: selectedMonth, year: selectedYear })
                    .startOf('month')
                    .format('YYYY-MM-DD')
            );
            setEndDate(
                dayjs()
                    .set({ month: selectedMonth, year: selectedYear })
                    .endOf('month')
                    .format('YYYY-MM-DD')
            );
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}>
            <Filter
                name="Track Report"
                onChange={handleFilterChange}
                staffOptions={staffOptions}
                selectedStaff={selectedStaff}
                setSelectedStaff={setSelectedStaff}
            />
            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '15px' }}>
                {!_.isEmpty(endedSession) ? (
                    endedSession.map((meet) => <TrackReportCard key={meet.id} data={meet} />)
                ) : (
                    <NoInformation />
                )}
            </div>
        </div>
    );
};

TrackReport.propTypes = {
    staffOptions: PropTypes.arrayOf(PropTypes.object),
    selectedStaff: PropTypes.string.isRequired,
    setSelectedStaff: PropTypes.func.isRequired,
};

export default TrackReport;

const TrackReportCard = ({ data }) => {
    const history = useHistory();

    const handleRedirect = () => {
        if (data.meet.type === 'one') {
            history.push({
                pathname: `/provider/session/report/${data.meet.id}`,
                state: {
                    prevPath: history.location.pathname,
                    prevTab: 4,
                },
            });
        }
        if (data.meet.type === 'group') {
            history.push({
                pathname: `/provider/session/group-report/${data.meet.id}`,
                state: {
                    prevPath: history.location.pathname,
                    prevTab: 4,
                },
            });
        }
    };
    return (
        <StyledCard style={{ padding: '20px 25px 20px 20px' }}>
            <StyledCardContent
                style={{
                    display: 'grid',
                    alignItems: 'center',
                    gridTemplateColumns: '70px 1px 1fr 1fr 1fr',
                    columnGap: '20px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <StyledDayContainer $backgroundColor={SESSION_COLORS[data.meet.type]}>
                        <Text $fontSize="18px" $color="white">
                            {dayjs(data?.date).format('DD')}
                        </Text>
                    </StyledDayContainer>
                    <Text
                        $fontSize="12px"
                        $color={SESSION_COLORS[data.meet.type]}
                        style={{ textTransform: 'uppercase' }}
                    >
                        {dayjs(data?.date).format('MMM, ddd')}
                    </Text>
                </div>
                <div
                    style={{
                        borderRight: '1px solid #EFEFEF',
                        height: '100%',
                    }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}>
                    <Tag
                        style={{ height: '21px' }}
                        $backgroundColor={SESSION_COLORS[data.meet.type]}
                    >
                        <Text $fontSize="12px" $color="white">
                            {SESSION_TYPE[data.meet.type]}
                        </Text>
                    </Tag>
                    <Text $fontSize="14px" $medium $ellipsis>
                        Activity name - {data?.meet?.name}
                    </Text>
                </div>
                <AvatarGroup
                    $size="50px"
                    data={data?.meet?.patients}
                    style={{ justifyContent: 'center' }}
                />
                <div style={{ justifySelf: 'end' }}>
                    <Button
                        variant="contained"
                        style={{ backgroundColor: '#5789E3', width: '190px', height: '38px' }}
                        onClick={handleRedirect}
                    >
                        <Text $color="white" $fontSize="12px" $medium>
                            View Track Report
                        </Text>
                    </Button>
                </div>
            </StyledCardContent>
        </StyledCard>
    );
};

TrackReportCard.propTypes = {
    data: PropTypes.object.isRequired,
};
